import React from 'react';
import Pagination from '@mui/material/Pagination';
import { type TableContainerProps } from '@mui/material/TableContainer';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import PageSizeSelect from '@/components/common/PageSizeSelect';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@/components/common/ResponsiveTable';
import type SortOrder from '@/constants/SortOrder';
import type { SortableFields } from '@/modules/QueryProvider';
import {
  generateKey,
  generateRowColumnKey,
  renderHeaderName,
} from '../lib/dataListUtils';
import type Column from '../types/Column';
import type DataListTableRowProps from '../types/DataListTableRowProps';
import type DynamicKey from '../types/DynamicKey';
import DataListTableBodyCell from './DataListTableBodyCell';
import DataListTableHeader from './DataListTableHeader';

export type DataListTableProps<DataType extends object> = {
  TableRowProps?:
    | DataListTableRowProps<DataType>
    | ((row: DataType) => DataListTableRowProps<DataType>);
  TableContainerProps?: TableContainerProps;
  columns: Column<DataType>[];
  data: DataType[];
  isLagging?: boolean;
  onPageChange?(event: React.ChangeEvent<unknown>, value: number): void;
  onPageSizeChange?(event: React.ChangeEvent<unknown>): void;
  onSortClick?(sortBy: Extract<keyof DataType, string>): void;
  page: number;
  pageSize: number;
  rowKey: Extract<keyof DataType, string> | ((row: DataType) => string);
  sortableFields?: SortableFields;
  sortBy?: Extract<keyof DataType, string>;
  sortOrder?: SortOrder;
  tableHeader?: React.ReactNode;
  totalRows: number;
  hidePageSize?: boolean;
};

export default function DataListTable<DataType extends object>({
  TableRowProps,
  TableContainerProps = {},
  columns,
  data,
  isLagging,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  onSortClick = () => {},
  page,
  pageSize,
  rowKey,
  sortableFields,
  sortBy,
  sortOrder,
  tableHeader,
  totalRows,
  hidePageSize = false,
}: DataListTableProps<DataType>) {
  const handleSortClick = React.useCallback(
    (columnKey: DynamicKey<DataType>) => {
      if (typeof columnKey === 'string' && sortableFields?.[columnKey]) {
        onSortClick(columnKey);
      }
    },
    [onSortClick, sortBy],
  );

  const totalPages = Math.ceil(totalRows / pageSize);
  const hasPagination = totalRows > data.length;

  return (
    <>
      {typeof tableHeader === 'string' && (
        <DataListTableHeader text={tableHeader} count={totalRows} />
      )}
      {tableHeader && typeof tableHeader !== 'string' && tableHeader}

      <TableContainer {...TableContainerProps}>
        <Table>
          <TableHead>
            <TableRow>
              {columns?.map(
                ({
                  TableHeaderCellProps,
                  align = 'left',
                  columnKey,
                  headerName,
                }) => {
                  const camelCasedSortBy = camelCase(sortBy);

                  return (
                    <TableCell
                      key={columnKey as string}
                      data-testid={`data-list-table-header-cell-${
                        columnKey as string
                      }`}
                      align={align}
                      sortDirection={
                        camelCasedSortBy === columnKey ? sortOrder : false
                      }
                      {...(TableHeaderCellProps && TableHeaderCellProps)}
                    >
                      {typeof columnKey === 'string' &&
                      sortableFields?.[columnKey] ? (
                        <TableSortLabel
                          active={camelCasedSortBy === columnKey}
                          data-testid={`data-list-table-header-${
                            columnKey as string
                          }-sort-button`}
                          direction={sortOrder}
                          onClick={() =>
                            handleSortClick(columnKey as DynamicKey<DataType>)
                          }
                        >
                          {renderHeaderName(headerName)}
                        </TableSortLabel>
                      ) : (
                        renderHeaderName(headerName)
                      )}
                    </TableCell>
                  );
                },
              )}
            </TableRow>
          </TableHead>
          <TableBody className={classNames({ 'opacity-60': isLagging })}>
            {data.map((row: DataType) => {
              const { onClick: handleTableRowClick, ...TableRowPropsRest } =
                typeof TableRowProps === 'function'
                  ? TableRowProps(row)
                  : TableRowProps || {};

              const key = generateKey(rowKey, row) as string;

              return (
                <TableRow
                  key={key}
                  data-testid={`data-list-table-row-${key}`}
                  {...(TableRowPropsRest && TableRowPropsRest)}
                  {...(handleTableRowClick && {
                    onClick: (
                      e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                    ) => handleTableRowClick(row, e),
                  })}
                >
                  {columns.map((column) => {
                    const { TableCellProps } = column;

                    return (
                      <DataListTableBodyCell<DataType>
                        TableCellProps={{
                          ...(TableCellProps && TableCellProps),
                        }}
                        key={generateRowColumnKey<DataType>(
                          row,
                          rowKey,
                          column.columnKey,
                        )}
                        column={column}
                        row={row}
                      />
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {(hasPagination || !hidePageSize) && (
        <div className="mt-6 md:grid md:grid-cols-[1fr_150px] md:pl-[150px]">
          {!hidePageSize && (
            <div className="text-center md:order-last md:col-start-2 md:text-right">
              <PageSizeSelect
                value={pageSize}
                className="ml-auto"
                onChange={onPageSizeChange}
              />
            </div>
          )}
          {hasPagination && (
            <div className="flex items-center justify-center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={onPageChange}
                shape="rounded"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
